import { Action, getModule, Module, VuexModule } from 'vuex-module-decorators';
import store from '@/store/index';
import { WS_URL as ApiUrl } from '@/config';
import axios from 'axios';
import { NotificationService } from '@/services/notification-service';

export type MailSchedulerRoutine = 'Newsletters' | 'Announcement';

export interface MailSchedulerOption {
  index: number;
  name: string;
}

export interface MailSchedulerOptions {
  newsletters: MailSchedulerOption[];
  announcements: MailSchedulerOption[];
}

export interface MailSchedulerRunResult {
  routine: MailSchedulerRoutine;
  dryRun: boolean;
  selectedName: string;
  recipients: string[];
  stats: { [key: string]: number };
  totalMails: number;
}

export interface RunMailSchedulerRequest {
  routine: MailSchedulerRoutine;
  index: number;
  send: boolean;
}

export interface MailPreview {
  name: string;
  html: string;
}

@Module({ dynamic: true, namespaced: true, store, name: 'mailSender' })
export class MailSenderModule extends VuexModule {
  @Action
  async getOptions(): Promise<MailSchedulerOptions> {
    try {
      const response = await axios.get<MailSchedulerOptions>(`${ApiUrl}/api/MailScheduler/options`);
      return response.data;
    } catch (error) {
      NotificationService.NotifyError('Could not load the mail sender options.');
      return { newsletters: [], announcements: [] };
    }
  }

  @Action
  async run(request: RunMailSchedulerRequest): Promise<MailSchedulerRunResult | null> {
    try {
      const response = await axios.post<MailSchedulerRunResult>(`${ApiUrl}/api/MailScheduler/run`, request);

      if (request.send) {
        const total = response.data.totalMails;
        NotificationService.NotifySuccess(`${total} email${total === 1 ? '' : 's'} sent.`);
      }

      return response.data;
    } catch (error) {
      NotificationService.NotifyError('Could not run the mail sender.');
      return null;
    }
  }

  @Action
  async getPreview(request: { routine: MailSchedulerRoutine; index: number }): Promise<MailPreview | null> {
    try {
      const response = await axios.get<MailPreview>(`${ApiUrl}/api/MailScheduler/preview`, {
        params: { routine: request.routine, index: request.index }
      });
      return response.data;
    } catch (error) {
      const message =
        axios.isAxiosError(error) && error.response
          ? `Could not load the newsletter preview (${error.response.status}).`
          : 'Could not load the newsletter preview.';
      NotificationService.NotifyError(message);
      return null;
    }
  }
}

export const MailSenderStore = getModule(MailSenderModule);
